import React, { useEffect, useState, useCallback } from 'react';
import { colors } from '../styles/colors';
import '../responsive-style.css';
import './SocialMediaPosterPage.css';
import { Logo } from '../components/Logo';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { auth } from '../firebase';
import { db, collection, addDoc, query, orderBy, limit, onSnapshot, doc, deleteDoc, getDoc, setDoc, updateDoc } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { publishPostToWordPress, deleteWordPressPost, fetchWordPressPosts } from '../services/wordpressService';
import BlankDashboardPage from './BlankDashboardPage';

// Helper function to get platform-specific colors - Simple solid colors only
const getPlatformColors = (platform) => {
  const colors = {
    'Instagram': {
      color: '#E4405F'
    },
    'Facebook': {
      color: '#1877F2'
    },
    'Twitter/X': {
      color: '#000000'
    },
    'LinkedIn': {
      color: '#0A66C2'
    },
    'Blog': {
      color: '#6366F1'
    }
  };
  return colors[platform] || colors['Blog'];
};

export const SocialMediaPosterPage = () => {
  const [aiMetadata, setAiMetadata] = useState({
    visualSuggestion: '',  // From instagram_visual_suggestion - shared for all platforms
    generatedAt: null,
    source: 'n8n'
  });

  const [posts, setPosts] = useState([
    {
      id: 1,
      platform: 'Instagram',
      content: '',
      imageUrls: [],
      notes: '',
      aiMetadata: {}
    },
    {
      id: 2,
      platform: 'Facebook',
      content: '',
      linkUrl: '',          // NEW: From facebook_link_url
      imageUrls: [],
      notes: '',
      aiMetadata: {}
    },
    {
      id: 3,
      platform: 'Twitter/X',
      content: '',
      thread: [],           // NEW: From twitter_thread
      imageUrls: [],
      notes: '',
      aiMetadata: {}
    },
    {
      id: 4,
      platform: 'LinkedIn',
      content: '',
      visibility: 'PUBLIC',           // NEW: From linkedin_visibility
      lifecycleState: 'PUBLISHED',    // NEW: From linkedin_lifecycle_state
      distribution: {                 // NEW: From linkedin_distribution_json
        feedDistribution: 'MAIN_FEED',
        targetEntities: [],
        thirdPartyDistributionChannels: []
      },
      imageUrls: [],
      notes: '',
      aiMetadata: {}
    }
  ]);

  const [blogPost, setBlogPost] = useState({
    title: '',
    content: '',
    excerpt: '',          // NEW: From blog_excerpt
    slug: '',             // NEW: From blog_slug
    status: 'draft',      // NEW: From blog_status
    featuredImageUrl: '',
    aiMetadata: {}
  });

  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [activeTab, setActiveTab] = useState('blog');
  const [campaigns, setCampaigns] = useState([]);

  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [message, setMessage] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [postHistory, setPostHistory] = useState([]); // Manual posts from 'posts' collection
  const [generatedPostsHistory, setGeneratedPostsHistory] = useState([]); // AI-generated posts from 'generatedPosts' collection
  const [user, setUser] = useState(null);
  const [userConfig, setUserConfig] = useState(null); // null = checking, object = config loaded
  const [isDashboardConfigured, setIsDashboardConfigured] = useState(null); // null = checking, true = configured, false = not configured
  const [wordpressPosts, setWordpressPosts] = useState([]);
  const [wordpressLoading, setWordpressLoading] = useState(false);
  const [linkedinConnected, setLinkedinConnected] = useState(false);
  const [linkedinUserInfo, setLinkedinUserInfo] = useState(null);

  const loadingMessages = [
    'Pondering...',
    'Thinking deeply...',
    'Pontificating...',
    'Consulting the AI...',
    'Crafting brilliance...',
    'Summoning creativity...',
    'Weaving words...',
    'Analyzing trends...',
    'Generating magic...',
    'Almost there...'
  ];

  useEffect(() => {
    // Listen for auth state changes - this waits for Firebase to initialize
    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      console.log('🔍 Auth state changed, current user:', currentUser);
      console.log('🔍 User UID:', currentUser?.uid);
      console.log('🔍 User email:', currentUser?.email);
      setUser(currentUser);

      if (currentUser) {
        try {
          console.log('🔍 Checking dashboard configuration for user:', currentUser.email);
          // Check if user has a dashboard configuration in Firestore
          const userDocRef = doc(db, 'users', currentUser.uid);
          const userDocSnap = await getDoc(userDocRef);

          if (userDocSnap.exists()) {
            // User document exists - check if dashboard is configured
            const userData = userDocSnap.data();
            console.log('✅ User document found:', userData);
            console.log('🔍 Dashboard configured value:', userData.dashboardConfigured);

            // Handle both boolean true and string "true" for dashboardConfigured
            const isConfigured = userData.dashboardConfigured === true || userData.dashboardConfigured === 'true';
            console.log('📊 Is dashboard configured?', isConfigured);
            setIsDashboardConfigured(isConfigured);
            setUserConfig(userData);

            if (isConfigured) {
              console.log('Dashboard is configured - loading user data');

              // Load post history from Firestore (manual posts)
              const postsQuery = query(
                collection(db, 'users', currentUser.uid, 'posts'),
                orderBy('createdAt', 'desc'),
                limit(50)
              );

              const unsubscribeSnapshot = onSnapshot(postsQuery, (snapshot) => {
                console.log('Firestore snapshot:', snapshot);
                const historyData = snapshot.docs.map(doc => ({
                  id: doc.id,
                  ...doc.data()
                }));
                console.log('History data loaded:', historyData);
                setPostHistory(historyData);
              }, (error) => {
                console.error('Firestore error:', error);
                setMessage('Error loading post history: ' + error.message);
              });

              // Load AI-generated posts from Firestore (generatedPosts collection)
              const generatedPostsQuery = query(
                collection(db, 'users', currentUser.uid, 'generatedPosts'),
                orderBy('createdAt', 'desc'),
                limit(50)
              );

              const unsubscribeGeneratedPosts = onSnapshot(generatedPostsQuery, (snapshot) => {
                console.log('Generated posts snapshot:', snapshot);
                const generatedData = snapshot.docs.map(doc => ({
                  id: doc.id,
                  ...doc.data()
                }));
                console.log('Generated posts loaded:', generatedData);
                setGeneratedPostsHistory(generatedData);
              }, (error) => {
                console.error('Firestore error loading generated posts:', error);
              });

              // Load campaigns
              const campaignsQuery = query(
                collection(db, 'users', currentUser.uid, 'campaigns'),
                orderBy('createdAt', 'desc'),
                limit(50)
              );

              const unsubscribeCampaigns = onSnapshot(campaignsQuery, (snapshot) => {
                const campaignsData = snapshot.docs.map(doc => ({
                  id: doc.id,
                  ...doc.data()
                }));
                setCampaigns(campaignsData);
              });

              // Store unsubscribe functions for cleanup
              return () => {
                unsubscribeSnapshot();
                unsubscribeGeneratedPosts();
                unsubscribeCampaigns();
              };
            } else {
              console.log('Dashboard not configured - showing blank dashboard');
              setIsDashboardConfigured(false);
            }
          } else {
            // User document doesn't exist - create it with default config
            console.log('User document not found - creating new user profile');

            const newUserData = {
              email: currentUser.email,
              displayName: currentUser.displayName || currentUser.email?.split('@')[0],
              photoURL: currentUser.photoURL || '',
              dashboardConfigured: false, // New users start with unconfigured dashboard
              createdAt: new Date(),
              lastLogin: new Date()
            };

            await setDoc(userDocRef, newUserData);
            console.log('New user profile created');

            setUserConfig(newUserData);
            setIsDashboardConfigured(false);
          }
        } catch (error) {
          console.error('❌ Error loading user configuration:', error);
          console.error('❌ Error code:', error.code);
          console.error('❌ Error message:', error.message);
          setMessage('Error loading dashboard configuration: ' + error.message);
          setIsDashboardConfigured(false);
        }
      } else {
        console.log('No current user found - redirecting to login');
        // Redirect to login if not authenticated
        window.location.href = '/logicpros';
      }
    });

    // Cleanup auth listener on unmount
    return () => unsubscribeAuth();
  }, []);

  // Fetch WordPress posts for Blog Posts Management
  const loadWordPressPosts = async () => {
    if (!user || !isDashboardConfigured) return; // Only load if user is authenticated and dashboard is configured
    
    setWordpressLoading(true);
    try {
      const result = await fetchWordPressPosts({
        perPage: 20,
        status: 'publish'
      });

      console.log('Fetch result:', result);
      console.log('Result success:', result.success);
      console.log('Result posts:', result.posts);

      if (result.success && Array.isArray(result.posts)) {
        // Format posts to match the expected structure
        const formattedPosts = result.posts.map(post => ({
          id: post.id.toString(),
          title: post.title.rendered,
          content: post.content.rendered,
          excerpt: post.excerpt.rendered,
          featuredImageUrl: post._embedded?.['wp:featuredmedia']?.[0]?.source_url || null,
          createdAt: post.date,
          modifiedAt: post.modified,
          slug: post.slug,
          link: post.link,
          status: post.status,
          author: post._embedded?.author?.[0]?.name || 'Unknown',
          wordpressPostId: post.id // Store WordPress post ID for deletion
        }));

        setWordpressPosts(formattedPosts);
      } else {
        console.error('Failed to load WordPress posts:', result.error || 'Invalid result format');
        console.error('Result structure:', result);
      }
    } catch (error) {
      console.error('Error loading WordPress posts:', error);
    } finally {
      setWordpressLoading(false);
    }
  };

  useEffect(() => {
    if (isDashboardConfigured) {
      loadWordPressPosts();
      loadLinkedInConnection();
    }
  }, [user, isDashboardConfigured]);

  // Load LinkedIn connection status from Firestore
  const loadLinkedInConnection = async () => {
    if (!user) return;

    try {
      const userDocRef = doc(db, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        if (userData.linkedinAccessToken && userData.linkedinUserInfo) {
          setLinkedinConnected(true);
          setLinkedinUserInfo(userData.linkedinUserInfo);
        }
      }
    } catch (error) {
      console.error('Error loading LinkedIn connection:', error);
    }
  };

  // Handle LinkedIn connection
  const handleConnectLinkedIn = async () => {
    try {
      setLoading(true);
      setMessage('Connecting to LinkedIn...');

      // Set up message listener BEFORE opening the window
      const messageHandler = async (event) => {
        console.log('Message received:', event.data);
        console.log('Message origin:', event.origin);
        console.log('Expected origin:', window.location.origin);

        // Temporarily accept messages from any origin for debugging
        // TODO: Re-enable origin check after debugging
        // if (event.origin !== window.location.origin) {
        //   console.warn('Received message from unexpected origin:', event.origin);
        //   return;
        // }

        if (event.data.type === 'linkedin-oauth-success') {
          console.log('LinkedIn OAuth success message received');
          console.log('Has access token:', !!event.data.access_token);
          console.log('Has user info:', !!event.data.userInfo);

          // Remove the event listener
          window.removeEventListener('message', messageHandler);

          try {
            // Save tokens to Firestore (only include defined values)
            const userDocRef = doc(db, 'users', user.uid);
            const linkedinData = {
              linkedinAccessToken: event.data.access_token,
              linkedinExpiresAt: Date.now() + (event.data.expires_in * 1000),
              linkedinUserInfo: event.data.userInfo,
              linkedinOrganizations: event.data.organizations || []
            };

            // Only add refresh token if it exists
            if (event.data.refresh_token) {
              linkedinData.linkedinRefreshToken = event.data.refresh_token;
              linkedinData.linkedinRefreshTokenExpiresAt = Date.now() + (event.data.refresh_token_expires_in * 1000);
            }

            await setDoc(userDocRef, linkedinData, { merge: true });

            setLinkedinConnected(true);
            setLinkedinUserInfo(event.data.userInfo);

            // Show success message with organization info
            if (event.data.organizations && event.data.organizations.length > 0) {
              const orgNames = event.data.organizations.map(org => org.name).join(', ');
              setMessage(`LinkedIn connected successfully! Can post to: ${orgNames}`);
            } else {
              setMessage('LinkedIn connected successfully! Will post to personal profile.');
            }
          } catch (err) {
            console.error('Error saving LinkedIn tokens:', err);
            setMessage('Error saving LinkedIn connection: ' + err.message);
          } finally {
            setLoading(false);
          }
        }
      };

      window.addEventListener('message', messageHandler);

      // Call serverless function to get authorization URL
      const response = await fetch('/api/linkedin-auth?action=initiate');
      const data = await response.json();

      if (!response.ok || !data.authUrl) {
        window.removeEventListener('message', messageHandler);
        throw new Error(data.message || 'Failed to generate authorization URL');
      }

      // Open LinkedIn OAuth in new window
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      const authWindow = window.open(
        data.authUrl,
        'LinkedIn Authorization',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      // Set a timeout to clean up if the user closes the window without completing auth
      const timeout = setTimeout(() => {
        window.removeEventListener('message', messageHandler);
        if (authWindow && !authWindow.closed) {
          authWindow.close();
        }
        setLoading(false);
        setMessage('LinkedIn connection timed out or was cancelled.');
      }, 300000); // 5 minutes

      // Clear timeout if we get a message
      const originalHandler = messageHandler;
      const wrappedHandler = async (event) => {
        clearTimeout(timeout);
        await originalHandler(event);
      };
      window.removeEventListener('message', messageHandler);
      window.addEventListener('message', wrappedHandler);

    } catch (error) {
      console.error('Error connecting to LinkedIn:', error);
      setMessage('Error connecting to LinkedIn: ' + error.message);
      setLoading(false);
    }
  };

  // Handle LinkedIn disconnection
  const handleDisconnectLinkedIn = async () => {
    if (!window.confirm('Are you sure you want to disconnect LinkedIn?')) {
      return;
    }

    try {
      setLoading(true);
      setMessage('Disconnecting LinkedIn...');

      // Remove tokens from Firestore
      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, {
        linkedinAccessToken: null,
        linkedinRefreshToken: null,
        linkedinExpiresAt: null,
        linkedinUserInfo: null
      }, { merge: true });

      setLinkedinConnected(false);
      setLinkedinUserInfo(null);
      setMessage('LinkedIn disconnected successfully.');
      setLoading(false);
    } catch (error) {
      console.error('Error disconnecting LinkedIn:', error);
      setMessage('Error disconnecting LinkedIn: ' + error.message);
      setLoading(false);
    }
  };

  // CSS is now imported from SocialMediaPosterPage.css

  const handleContentChange = useCallback((postId, content) => {
    setPosts(prevPosts =>
      prevPosts.map(post =>
        post.id === postId ? { ...post, content } : post
      )
    );
  }, []);

  const handleImageChange = useCallback((postId, imageUrl) => {
    setPosts(prevPosts =>
      prevPosts.map(post =>
        post.id === postId ? { ...post, imageUrls: imageUrl ? [imageUrl] : [] } : post
      )
    );
  }, []);

  const handleNotesChange = useCallback((postId, notes) => {
    setPosts(prevPosts =>
      prevPosts.map(post =>
        post.id === postId ? { ...post, notes } : post
      )
    );
  }, []);

  const handlePostFieldChange = useCallback((postId, field, value) => {
    setPosts(prevPosts =>
      prevPosts.map(post =>
        post.id === postId ? { ...post, [field]: value } : post
      )
    );
  }, []);

  // Blog post handlers
  const handleBlogChange = useCallback((field, value) => {
    setBlogPost(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleDeleteBlogPost = async (postId) => {
    if (!user) {
      setMessage('Error: You must be logged in to delete posts.');
      return;
    }

    // Find the post to get WordPress post ID
    const postToDelete = postHistory.find(post => post.id === postId);
    
    if (!window.confirm('Are you sure you want to delete this blog post? This will delete it from both the dashboard and WordPress. This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    setMessage('Deleting blog post...');

    try {
      let wordpressDeleteResult = null;
      
      // Delete from WordPress if it has a WordPress post ID
      if (postToDelete?.wordpressPostId) {
        setMessage('Deleting from WordPress...');
        wordpressDeleteResult = await deleteWordPressPost(postToDelete.wordpressPostId);
        
        if (!wordpressDeleteResult.success) {
          throw new Error(`WordPress deletion failed: ${wordpressDeleteResult.error}`);
        }
      }

      // Delete from Firestore
      setMessage('Deleting from dashboard...');
      await deleteDoc(doc(db, 'users', user.uid, 'posts', postId));

      // Update local state to remove the deleted post
      setPostHistory(prev => prev.filter(post => post.id !== postId));

      const successMessage = wordpressDeleteResult
        ? '✅ Blog post deleted successfully from both dashboard and WordPress!'
        : '✅ Blog post deleted successfully from dashboard!';
      
      setMessage(successMessage);
      setTimeout(() => setMessage(''), 3000);

    } catch (error) {
      console.error('Error deleting blog post:', error);
      setMessage(`❌ Failed to delete blog post: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWordPressPost = async (postId) => {
    if (!user) {
      setMessage('Error: You must be logged in to delete posts.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this blog post from WordPress? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    setMessage('Deleting from WordPress...');

    try {
      // Delete from WordPress using the WordPress post ID
      const deleteResult = await deleteWordPressPost(parseInt(postId));
      
      if (!deleteResult.success) {
        throw new Error(deleteResult.error);
      }

      // Update local state to remove the deleted post
      setWordpressPosts(prev => prev.filter(post => post.id !== postId));

      setMessage('✅ Blog post deleted successfully from WordPress!');
      setTimeout(() => setMessage(''), 3000);

    } catch (error) {
      console.error('Error deleting WordPress post:', error);
      setMessage(`❌ Failed to delete post from WordPress: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePost = async (post) => {
    if (!user) {
      setMessage('Error: You must be logged in to save posts.');
      return;
    }

    if (!post.content.trim()) {
      setMessage('Error: Post content cannot be empty.');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      console.log(`Saving post to ${post.platform}:`, post);

      let platformPostId = null;
      let platformUrl = null;
      let postStatus = 'published';

      // If LinkedIn, attempt to post via API
      if (post.platform === 'LinkedIn' && linkedinConnected) {
        try {
          setMessage(`Publishing to LinkedIn...`);

          // Get LinkedIn credentials from Firestore
          const userDocRef = doc(db, 'users', user.uid);
          const userDocSnap = await getDoc(userDocRef);

          if (!userDocSnap.exists() || !userDocSnap.data().linkedinAccessToken) {
            throw new Error('LinkedIn not connected. Please connect your LinkedIn account.');
          }

          const userData = userDocSnap.data();

          // Determine author URN - prefer organization if available
          let authorUrn = `urn:li:person:${userData.linkedinUserInfo.sub}`; // Default to personal

          if (userData.linkedinOrganizations && userData.linkedinOrganizations.length > 0) {
            // Use the first organization (Logic Pros Solutions Inc.)
            const org = userData.linkedinOrganizations[0];
            authorUrn = org.urn;
            console.log(`Posting to organization: ${org.name} (${org.urn})`);
          } else {
            console.log(`Posting to personal profile: ${userData.linkedinUserInfo.name}`);
          }

          // Post to LinkedIn via serverless function as DRAFT
          const linkedinResponse = await fetch('/api/linkedin-post', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              access_token: userData.linkedinAccessToken,
              authorUrn: authorUrn,
              content: post.content,
              visibility: 'PUBLIC',
              isDraft: true  // Save as draft instead of publishing live
            })
          });

          const linkedinData = await linkedinResponse.json();

          if (!linkedinResponse.ok) {
            throw new Error(linkedinData.message || 'Failed to post to LinkedIn');
          }

          platformPostId = linkedinData.postId;
          console.log('LinkedIn post published successfully:', platformPostId);

        } catch (linkedinError) {
          console.error('LinkedIn posting error:', linkedinError);
          postStatus = 'failed';
          setMessage(`⚠️ Saved locally but LinkedIn posting failed: ${linkedinError.message}`);
        }
      }

      // Save to Firestore with enhanced data structure
      await addDoc(collection(db, 'users', user.uid, 'posts'), {
        type: 'social',
        platform: post.platform,
        content: post.content,
        imageUrls: post.imageUrls || [],
        notes: post.notes || '',

        // Status tracking
        status: postStatus,

        // Timestamps
        createdAt: new Date(),
        updatedAt: new Date(),
        publishedAt: new Date(),

        // Platform tracking
        platformPostId: platformPostId,
        platformUrl: platformUrl,

        // User tracking
        createdBy: user.uid,

        // AI metadata
        aiMetadata: post.aiMetadata || {},

        // Scheduling (null for now)
        scheduledDate: null,

        // Optional fields
        campaignTags: []
      });

      if (postStatus !== 'failed') {
        setMessage(`✅ Post ${post.platform === 'LinkedIn' && linkedinConnected ? 'saved as draft on' : 'saved to'} ${post.platform} successfully!`);
      }

      // Clear the form for this platform
      setPosts(prevPosts =>
        prevPosts.map(p =>
          p.id === post.id ? { ...p, content: '', imageUrls: [], notes: '' } : p
        )
      );

      // Auto-hide success message after 3 seconds
      setTimeout(() => setMessage(''), 3000);

    } catch (error) {
      console.error('Error saving post:', error);
      setMessage(`❌ Failed to save post: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBlogPost = async () => {
    if (!user) {
      setMessage('Error: You must be logged in to publish posts.');
      return;
    }

    if (!blogPost.title.trim() || !blogPost.content.trim()) {
      setMessage('Error: Blog title and content are required.');
      return;
    }

    setLoading(true);
    setMessage('Publishing to WordPress...');

    try {
      console.log('Publishing blog post to WordPress:', blogPost);

      // First, publish to WordPress
      const wordpressResult = await publishPostToWordPress({
        title: blogPost.title,
        content: blogPost.content,
        featuredImageUrl: blogPost.featuredImageUrl,
        status: 'publish'
      });

      if (!wordpressResult.success) {
        throw new Error(wordpressResult.message);
      }

      // Save to Firestore with WordPress publication data
      await addDoc(collection(db, 'users', user.uid, 'posts'), {
        type: 'blog',
        title: blogPost.title,
        content: blogPost.content,
        featuredImageUrl: blogPost.featuredImageUrl || '',

        // Status tracking - published to WordPress
        status: 'published',

        // Timestamps
        createdAt: new Date(),
        updatedAt: new Date(),
        publishedAt: new Date(),

        // WordPress publishing info
        wordpressPostId: wordpressResult.post.id,
        publishedUrl: wordpressResult.post.link,
        wordpressSlug: wordpressResult.post.slug,

        // User tracking
        createdBy: user.uid,

        // AI metadata
        aiMetadata: blogPost.aiMetadata || {},

        // Scheduling
        scheduledDate: null
      });

      setMessage(`✅ Blog post published successfully! View it at: ${wordpressResult.post.link}`);

      // Clear the blog form
      setBlogPost({
        title: '',
        content: '',
        featuredImageUrl: '',
        aiMetadata: {}
      });

      // Refresh WordPress posts list to show the new post
      setTimeout(() => {
        loadWordPressPosts();
      }, 1000);

      // Auto-hide success message after 5 seconds (longer for WordPress posts)
      setTimeout(() => setMessage(''), 5000);

    } catch (error) {
      console.error('Error publishing blog post:', error);
      setMessage(`❌ Failed to publish blog post: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePosts = async () => {
    // Check if Firestore is available
    if (!db) {
      setMessage('❌ Firebase is not initialized. Please refresh the page.');
      return;
    }

    if (!user) {
      setMessage('❌ You must be logged in to generate posts.');
      return;
    }

    setLoading(true);
    setMessage('');

    // Start rotating through loading messages
    let messageIndex = 0;
    setLoadingMessage(loadingMessages[0]);

    const messageInterval = setInterval(() => {
      messageIndex = (messageIndex + 1) % loadingMessages.length;
      setLoadingMessage(loadingMessages[messageIndex]);
    }, 2000); // Change message every 2 seconds

    try {
      console.log('Triggering n8n workflow...');

      // Trigger the n8n workflow (will respond immediately)
      const response = await fetch('https://n8n.logicpros.ca/webhook/3f8e4aac-da20-42e1-ad5d-3aa49de2c98a', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'generate_posts',
          userId: user?.uid,
          timestamp: new Date().toISOString(),
          platforms: posts.map(p => p.platform)
        })
      });

      console.log('Sent userId to n8n:', user?.uid);

      if (!response.ok) {
        throw new Error(`Workflow failed with status: ${response.status}`);
      }

      const data = await response.json();
      console.log('n8n workflow response:', data);

      // Check if workflow is processing asynchronously
      if (data.status === 'processing') {
        console.log('Workflow is processing in background, setting up real-time listener...');

        // Set up real-time listener for newly generated posts
        // This is more efficient than polling - Firebase pushes updates instantly
        // CRITICAL FIX: Capture EXACT timestamp when button was clicked
        // This prevents loading old posts from previous generations
        const workflowStartTime = new Date();

        // Temporarily remove orderBy to test if index is the issue
        // We'll get all docs and sort in memory
        const recentPostsQuery = collection(db, 'users', user.uid, 'generatedPosts');

        console.log('Setting up listener for collection path:', `users/${user.uid}/generatedPosts`);
        console.log('Workflow start time:', workflowStartTime.toISOString());
        console.log('Will only populate form with posts created AFTER this timestamp');

        // Test: Try to manually fetch documents to see if they exist
        import('firebase/firestore').then(({ getDocs }) => {
          getDocs(recentPostsQuery).then(testSnapshot => {
            console.log('TEST: Manual getDocs() query result:');
            console.log('TEST: Number of docs found:', testSnapshot.size);
            console.log('TEST: Docs:', testSnapshot.docs.map(d => ({ id: d.id, data: d.data() })));
          }).catch(err => {
            console.error('TEST: Error fetching docs:', err);
          });
        });

        // Set up real-time listener
        const unsubscribe = onSnapshot(
          recentPostsQuery,
          async (snapshot) => {
            console.log('========== FIREBASE SNAPSHOT UPDATE ==========');
            console.log('Received real-time update from Firestore');
            console.log('Snapshot empty?', snapshot.empty);
            console.log('Number of docs:', snapshot.docs.length);
            console.log('Snapshot metadata:', {
              fromCache: snapshot.metadata.fromCache,
              hasPendingWrites: snapshot.metadata.hasPendingWrites
            });
            console.log('Snapshot size:', snapshot.size);
            console.log('Snapshot docChanges:', snapshot.docChanges().map(change => ({
              type: change.type,
              docId: change.doc.id
            })));

            // CRITICAL FIX: Only respond to newly ADDED documents, not initial query results
            // This prevents auto-filling with existing old posts
            const addedDocs = snapshot.docChanges().filter(change => change.type === 'added');
            console.log('Number of newly added docs:', addedDocs.length);

            if (addedDocs.length > 0) {
              // Get the newly added documents and sort by createdAt
              const newDocs = addedDocs
                .map(change => ({ id: change.doc.id, ...change.doc.data() }))
                .sort((a, b) => {
                  const aTime = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
                  const bTime = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
                  return bTime - aTime; // Descending order (newest first)
                });

              console.log('Newly added documents:', newDocs);
              const latestPost = newDocs[0];
              console.log('Latest newly added post:', latestPost);

              // Check if this post was created after we triggered the workflow
              // createdAt might be a Firestore Timestamp or ISO string from n8n
              const postCreatedAt = latestPost.createdAt?.toDate ? latestPost.createdAt.toDate() : new Date(latestPost.createdAt);
              console.log('Post created at:', postCreatedAt);
              console.log('Workflow start time:', workflowStartTime);
              console.log('Is this a NEW post from THIS workflow run?', postCreatedAt > workflowStartTime);

              // CRITICAL FIX: Only populate form if post was created AFTER button click
              // This prevents auto-filling with old posts from previous generations
              if (postCreatedAt > workflowStartTime) {
                console.log('Found newly generated posts!', latestPost);

                // Stop listening
                unsubscribe();
                clearInterval(messageInterval);

                // Parse the generated content from Firestore
                // Note: Fields are stored at top level, not nested under 'data'
                const generatedData = latestPost;

                // Update forms with generated content
                if (generatedData) {
                  // Load shared AI metadata (visual suggestion)
                  setAiMetadata({
                    visualSuggestion: generatedData.instagram_visual_suggestion || '',
                    generatedAt: new Date().toISOString(),
                    source: 'n8n'
                  });

                  // Parse social media posts from n8n structure
                  setPosts(prevPosts =>
                    prevPosts.map(post => {
                    let generatedContent = '';
                    let generatedImages = [];
                    let metadata = {};
                    let additionalFields = {};

                    switch (post.platform) {
                      case 'Instagram':
                        // Support BOTH new nested structure (socialMedia.instagram) AND old flat structure (instagram_caption)
                        if (generatedData.socialMedia?.instagram?.caption || generatedData.instagram_caption) {
                          generatedContent = generatedData.socialMedia?.instagram?.caption || generatedData.instagram_caption;
                          const imageUrl = generatedData.socialMedia?.instagram?.imageUrl || generatedData.instagram_visual_suggestion;
                          if (imageUrl) {
                            generatedImages = [imageUrl];
                          }
                          metadata = {
                            hashtags: generatedData.socialMedia?.instagram?.hashtags || generatedData.instagram_hashtags || [],
                            charCount: generatedData.socialMedia?.instagram?.characterCount || generatedData.instagram_char_count || 0
                          };
                        }
                        break;
                      case 'Facebook':
                        // Support BOTH new nested structure (socialMedia.facebook) AND old flat structure (facebook_post)
                        if (generatedData.socialMedia?.facebook?.post || generatedData.facebook_post) {
                          generatedContent = generatedData.socialMedia?.facebook?.post || generatedData.facebook_post;
                          additionalFields = {
                            linkUrl: generatedData.socialMedia?.facebook?.linkUrl || generatedData.facebook_link_url || ''
                          };
                          metadata = {
                            charCount: generatedData.socialMedia?.facebook?.characterCount || generatedData.facebook_char_count || 0,
                            hasLink: generatedData.socialMedia?.facebook?.includesLink || generatedData.facebook_has_link || false
                          };
                        }
                        break;
                      case 'Twitter/X':
                        // Support BOTH new nested structure (socialMedia.twitter) AND old flat structure (twitter_thread)
                        const twitterThread = generatedData.socialMedia?.twitter?.thread || generatedData.twitter_thread;
                        if (twitterThread && Array.isArray(twitterThread)) {
                          generatedContent = twitterThread.join('\n\n---\n\n');
                          additionalFields = {
                            thread: twitterThread
                          };
                          metadata = {
                            tweetCount: generatedData.socialMedia?.twitter?.characterCounts?.length || generatedData.twitter_tweet_count || twitterThread.length,
                            originalThread: twitterThread
                          };
                        }
                        break;
                      case 'LinkedIn':
                        // Support BOTH new nested structure (socialMedia.linkedin) AND old flat structure (linkedin_post)
                        if (generatedData.socialMedia?.linkedin?.post || generatedData.linkedin_post) {
                          generatedContent = generatedData.socialMedia?.linkedin?.post || generatedData.linkedin_post;

                          // Parse distribution JSON
                          let distribution = {
                            feedDistribution: 'MAIN_FEED',
                            targetEntities: [],
                            thirdPartyDistributionChannels: []
                          };

                          if (generatedData.linkedin_distribution_json) {
                            try {
                              distribution = JSON.parse(generatedData.linkedin_distribution_json);
                            } catch (e) {
                              console.error('Failed to parse linkedin_distribution_json:', e);
                            }
                          }

                          additionalFields = {
                            visibility: generatedData.socialMedia?.linkedin?.visibility || generatedData.linkedin_visibility || 'PUBLIC',
                            lifecycleState: generatedData.socialMedia?.linkedin?.lifecycleState || generatedData.linkedin_lifecycle_state || 'PUBLISHED',
                            distribution: distribution
                          };
                          metadata = {
                            hashtags: generatedData.socialMedia?.linkedin?.hashtags || generatedData.linkedin_hashtags || []
                          };
                        }
                        break;
                    }

                    if (generatedContent) {
                      return {
                        ...post,
                        ...additionalFields,
                        content: generatedContent,
                        imageUrls: generatedImages,
                        aiMetadata: {
                          ...metadata,
                          generatedAt: new Date().toISOString(),
                          source: 'n8n'
                        }
                      };
                    }
                      return post;
                    })
                  );

                  // Parse blog post from n8n structure
                  // Support BOTH new nested structure (blogPost.title/content) AND old flat structure (polished_title/polished_post)
                  const blogTitle = generatedData.blogPost?.title || generatedData.polished_title;
                  const blogContent = generatedData.blogPost?.content || generatedData.polished_post;
                  const featuredImage = generatedData.blogPost?.featuredImageUrl || generatedData.featured_image_url;

                  if (blogTitle && blogContent) {
                    setBlogPost(prev => ({
                      ...prev,
                      title: blogTitle || '',
                      content: blogContent || '',
                      excerpt: generatedData.blogPost?.excerpt || generatedData.blog_excerpt || '',
                      slug: generatedData.blogPost?.slug || generatedData.blog_slug || '',
                      status: generatedData.blogPost?.status || generatedData.blog_status || 'draft',
                      featuredImageUrl: featuredImage || '',
                      aiMetadata: {
                        generatedAt: new Date().toISOString(),
                        source: 'n8n',
                        wordCount: generatedData.blogPost?.wordCount || generatedData.polished_word_count || 0
                      }
                    }));
                  }
                }

                // AUTO-SAVE to campaigns collection
                console.log('🔍 DEBUG: About to auto-save campaign');
                console.log('🔍 DEBUG: latestPost data:', latestPost);
                console.log('🔍 DEBUG: user.uid:', user.uid);

                try {
                  // Transform data from Firebase flat structure to nested campaign structure
                  const generatedData = latestPost;

                  const campaignData = {
                    title: generatedData.blogPost?.title || generatedData.polished_title || 'Untitled Campaign',
                    blogPost: {
                      title: generatedData.blogPost?.title || generatedData.polished_title || '',
                      content: generatedData.blogPost?.content || generatedData.polished_post || '',
                      excerpt: generatedData.blogPost?.excerpt || generatedData.blog_excerpt || '',
                      slug: generatedData.blogPost?.slug || generatedData.blog_slug || '',
                      status: generatedData.blogPost?.status || generatedData.blog_status || 'draft',
                      featuredImageUrl: generatedData.blogPost?.featuredImageUrl || generatedData.featured_image_url || '',
                      wordCount: generatedData.blogPost?.wordCount || generatedData.polished_word_count || 0
                    },
                    socialMedia: {
                      facebook: {
                        post: generatedData.socialMedia?.facebook?.post || generatedData.facebook_post || '',
                        linkUrl: generatedData.socialMedia?.facebook?.linkUrl || generatedData.facebook_link_url || '',
                        charCount: generatedData.socialMedia?.facebook?.charCount || generatedData.facebook_char_count || 0
                      },
                      instagram: {
                        caption: generatedData.socialMedia?.instagram?.caption || generatedData.instagram_caption || '',
                        visual: generatedData.socialMedia?.instagram?.visual || generatedData.instagram_visual || '',
                        hashtags: generatedData.socialMedia?.instagram?.hashtags || generatedData.instagram_hashtags || [],
                        charCount: generatedData.socialMedia?.instagram?.charCount || generatedData.instagram_char_count || 0
                      },
                      linkedin: {
                        post: generatedData.socialMedia?.linkedin?.post || generatedData.linkedin_post || '',
                        visibility: generatedData.socialMedia?.linkedin?.visibility || generatedData.linkedin_visibility || 'PUBLIC',
                        lifecycleState: generatedData.socialMedia?.linkedin?.lifecycleState || generatedData.linkedin_lifecycle_state || 'PUBLISHED',
                        hashtags: generatedData.socialMedia?.linkedin?.hashtags || generatedData.linkedin_hashtags || []
                      },
                      twitter: {
                        thread: generatedData.socialMedia?.twitter?.thread || generatedData.twitter_thread || [],
                        tweetCount: generatedData.socialMedia?.twitter?.tweetCount || generatedData.twitter_tweet_count || 0
                      }
                    },
                    createdAt: new Date(),
                    updatedAt: new Date(),
                    publishingStatus: {
                      blog: false,
                      facebook: false,
                      instagram: false,
                      linkedin: false,
                      twitter: false
                    }
                  };

                  console.log('🔍 DEBUG: campaignData to save:', campaignData);

                  const campaignRef = await addDoc(
                    collection(db, 'users', user.uid, 'campaigns'),
                    campaignData
                  );

                  console.log('✅ Campaign auto-saved with ID:', campaignRef.id);
                  console.log('✅ Setting selectedCampaign to:', { id: campaignRef.id, ...campaignData });

                  // Auto-select the newly created campaign
                  setSelectedCampaign({
                    id: campaignRef.id,
                    ...campaignData
                  });
                  setActiveTab('blog'); // Default to blog tab

                  console.log('✅ selectedCampaign should now be set - tabs should appear!');
                  setMessage('✅ Campaign created and ready to edit!');
                } catch (error) {
                  console.error('❌ Error saving campaign:', error);
                  console.error('❌ Error details:', error.message, error.stack);
                  setMessage('✅ Posts generated successfully! Review and edit below.');
                }

                setLoading(false);
              }
            }
          },
          (error) => {
            // Error callback for onSnapshot
            console.error('Firebase listener error:', error);
            console.error('Error code:', error.code);
            console.error('Error message:', error.message);
            clearInterval(messageInterval);
            setLoading(false);
            setMessage(`❌ Firebase error: ${error.message}`);
          }
        );

        // Set a timeout to stop listening after 10 minutes
        setTimeout(() => {
          unsubscribe();
          clearInterval(messageInterval);
          setLoading(false);
          setMessage('⏱️ Generation is taking longer than expected. Please check back shortly.');
        }, 600000); // 10 minutes

        // IMPORTANT: Don't execute the finally block when using real-time listener
        // The listener handles loading state itself
        return; // Exit early since we're using real-time listener
      }

      // If workflow returned data immediately (old synchronous pattern)
      console.log('n8n workflow response:', data);

      // Check if n8n returned generated posts in the expected format
      if (data && data.length > 0) {
        const responseData = data[0];

        // Parse social media posts
        if (responseData.social) {
          const socialData = responseData.social;
          console.log('Parsed social data:', socialData);

          // Update the post forms with generated content
          setPosts(prevPosts =>
            prevPosts.map(post => {
              let generatedContent = '';
              let generatedImages = [];
              let metadata = {};

              // Map each platform to the correct data from n8n response
              switch (post.platform) {
                case 'Instagram':
                  if (socialData.instagram) {
                    generatedContent = socialData.instagram.caption || '';
                    if (socialData.instagram.visual) {
                      generatedImages = [socialData.instagram.visual];
                    }
                    metadata = {
                      hashtags: socialData.instagram.hashtags || [],
                      charCount: socialData.instagram.charCount || 0
                    };
                  }
                  break;
                case 'Facebook':
                  if (socialData.facebook) {
                    generatedContent = socialData.facebook.post || '';
                    metadata = {
                      charCount: socialData.facebook.charCount || 0,
                      hasLink: socialData.facebook.hasLink || false
                    };
                  }
                  break;
                case 'Twitter/X':
                  if (socialData.twitter && socialData.twitter.thread) {
                    // Join Twitter thread array into single string with separators
                    generatedContent = socialData.twitter.thread.join('\n\n---\n\n');
                    metadata = {
                      tweetCount: socialData.twitter.tweetCount || socialData.twitter.thread.length,
                      originalThread: socialData.twitter.thread
                    };
                  }
                  break;
                case 'LinkedIn':
                  if (socialData.linkedin) {
                    generatedContent = socialData.linkedin.post || '';
                    metadata = {
                      hashtags: socialData.linkedin.hashtags || []
                    };
                  }
                  break;
              }

              // Update post content and image if generated content exists
              if (generatedContent) {
                return {
                  ...post,
                  content: generatedContent,
                  imageUrls: generatedImages,
                  aiMetadata: {
                    ...metadata,
                    generatedAt: new Date().toISOString(),
                    source: 'n8n'
                  }
                };
              }
              return post;
            })
          );
        }

        // Parse blog post
        if (responseData.polished) {
          console.log('Parsed blog post data:', responseData.polished);

          setBlogPost(prev => ({
            ...prev,
            title: responseData.polished.title || '',
            content: responseData.polished.content || responseData.polished,
            excerpt: responseData.polished.excerpt || '',
            slug: responseData.polished.slug || '',
            aiMetadata: {
              generatedAt: new Date().toISOString(),
              source: 'n8n'
            }
          }));
        }

        setMessage('✅ Posts generated successfully! Review and edit below.');
      } else {
        // If no posts data, just show success
        setMessage('✅ Workflow completed successfully!');
      }

      // Auto-hide success message after 5 seconds
      setTimeout(() => setMessage(''), 5000);

      // Only clear interval and loading for synchronous workflows
      clearInterval(messageInterval);
      setLoading(false);

    } catch (error) {
      // Clear the message interval on error
      clearInterval(messageInterval);
      console.error('Error triggering n8n workflow:', error);
      setMessage(`❌ Failed to trigger workflow: ${error.message}`);
      setLoading(false);
    }
  };

  // Save campaign changes to Firestore
  const handleSaveCampaign = async () => {
    if (!selectedCampaign || !user) {
      setMessage('❌ No campaign selected');
      return;
    }

    try {
      setLoading(true);
      setMessage('Saving campaign...');

      const campaignRef = doc(db, 'users', user.uid, 'campaigns', selectedCampaign.id);

      await updateDoc(campaignRef, {
        ...selectedCampaign,
        updatedAt: new Date()
      });

      setMessage('✅ Campaign saved successfully!');

      // Auto-hide success message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving campaign:', error);
      setMessage(`❌ Failed to save campaign: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getPlatformIcon = useCallback((platform) => {
    const icons = {
      'Instagram': (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="2" y="2" width="20" height="20" rx="5" stroke="#E4405F" strokeWidth="2" fill="none"/>
          <circle cx="12" cy="12" r="4" stroke="#E4405F" strokeWidth="2"/>
          <circle cx="17.5" cy="6.5" r="1.5" fill="#E4405F"/>
        </svg>
      ),
      'Facebook': (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 17.9895 4.3882 22.954 10.125 23.8542V15.4688H7.07812V12H10.125V9.35625C10.125 6.34875 11.9166 4.6875 14.6576 4.6875C15.9701 4.6875 17.3438 4.92188 17.3438 4.92188V7.875H15.8306C14.34 7.875 13.875 8.80008 13.875 9.75V12H17.2031L16.6711 15.4688H13.875V23.8542C19.6118 22.954 24 17.9895 24 12Z" fill="#1877F2"/>
        </svg>
      ),
      'Twitter/X': (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" fill="black"/>
        </svg>
      ),
      'LinkedIn': (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="#0A66C2"/>
        </svg>
      )
    };
    return icons[platform] || (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="2" width="20" height="20" rx="3" stroke="currentColor" strokeWidth="2"/>
        <path d="M8 12h8M12 8v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    );
  }, []);

  // Show loading state while checking dashboard configuration
  if (isDashboardConfigured === null) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8f9fa',
        position: 'relative'
      }}>
        <div className="loading-content">
          <div className="spinner"></div>
          <h2 className="loading-text">Loading Dashboard</h2>
          <p style={{
            fontSize: '0.9rem',
            color: '#6b7280',
            marginTop: '10px'
          }}>Checking authentication and configuration...</p>
        </div>
      </div>
    );
  }

  // Show blank dashboard for unconfigured users
  if (isDashboardConfigured === false) {
    return <BlankDashboardPage />;
  }

  // Show full dashboard for configured users
  return (
    <>
      {/* Standard Header - matching the rest of the site */}
      <header className="sticky-header">
        <div className="header-content">
          <div className="logo">
            <Logo />
          </div>
          <Navigation />
        </div>
      </header>

      <div className="dashboard-container">
        {/* Header Section */}
        <div className="dashboard-header">
          <div className="dashboard-title">
            <h1>Social Media Dashboard</h1>
            <p>Create and manage social media posts for Instagram, Facebook, Twitter/X, and LinkedIn <span className="ai-indicator">AI Powered</span></p>
          </div>
        </div>

        {/* LinkedIn Connection Status */}
        <div style={{
          background: linkedinConnected ? '#d1fae5' : '#fef3c7',
          border: `1px solid ${linkedinConnected ? '#10b981' : '#f59e0b'}`,
          borderRadius: '8px',
          padding: '16px',
          margin: '20px 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="#0A66C2" xmlns="http://www.w3.org/2000/svg">
              <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"/>
            </svg>
            <div>
              <div style={{ fontWeight: '600', color: '#1a1a2e' }}>
                {linkedinConnected ? 'LinkedIn Connected' : 'LinkedIn Not Connected'}
              </div>
              {linkedinConnected && linkedinUserInfo && (
                <div style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '4px' }}>
                  Connected as {linkedinUserInfo.name}
                </div>
              )}
              {!linkedinConnected && (
                <div style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '4px' }}>
                  Connect your LinkedIn account to publish posts automatically
                </div>
              )}
            </div>
          </div>
          <button
            onClick={linkedinConnected ? handleDisconnectLinkedIn : handleConnectLinkedIn}
            disabled={loading}
            style={{
              background: linkedinConnected ? '#ef4444' : '#0A66C2',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 20px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
          >
            {linkedinConnected ? 'Disconnect LinkedIn' : 'Connect LinkedIn'}
          </button>
        </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="loading-overlay">
          <div className="loading-content">
              <div className="spinner"></div>
              <p className="loading-text">{loadingMessage}</p>
            </div>
        </div>
      )}

      {/* UNIFIED DASHBOARD LAYOUT */}
      <div className="posts-container">
        {message && (
          <div className={`message ${message.includes('success') ? 'success-message' : 'error-message'}`}>
            {message}
          </div>
        )}

        {/* TOP: Big "Generate New Campaign" Button - Centered */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <button
            className="generate-button full-width-button"
            onClick={handleGeneratePosts}
            disabled={loading}
            style={{
              fontSize: '1.2rem',
              padding: '20px 40px',
              maxWidth: '500px',
              margin: '0 auto',
              display: 'inline-block'
            }}
          >
            {loading ? (
              <>
                <div className="spinner small-spinner" style={{
                  borderTop: '3px solid transparent',
                  borderTopColor: 'white'
                }}></div>
                Generating...
              </>
            ) : (
              <>
                🚀 Generate New Campaign
              </>
            )}
          </button>
        </div>

        {/* BELOW THAT: Previous Campaigns Section - LIST */}
        <div style={{ marginBottom: '40px' }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            marginBottom: '20px',
            color: '#1a1a2e'
          }}>
            Previous Campaigns
          </h2>

          {campaigns.length === 0 ? (
            <div style={{
              padding: '30px',
              textAlign: 'center',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <p style={{ color: '#6b7280', margin: 0 }}>
                No campaigns yet. Click "Generate New Campaign" to create your first one!
              </p>
            </div>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              {campaigns.map(campaign => (
                <div
                  key={campaign.id}
                  onClick={() => {
                    setSelectedCampaign(campaign);
                    setActiveTab('blog');
                  }}
                  style={{
                    padding: '16px 20px',
                    backgroundColor: selectedCampaign?.id === campaign.id ? '#e0e7ff' : '#ffffff',
                    border: selectedCampaign?.id === campaign.id ? '2px solid #6366f1' : '1px solid #e5e7eb',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedCampaign?.id !== campaign.id) {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedCampaign?.id !== campaign.id) {
                      e.currentTarget.style.backgroundColor = '#ffffff';
                    }
                  }}
                >
                  <div>
                    <div style={{
                      fontWeight: '600',
                      fontSize: '1rem',
                      color: '#1a1a2e',
                      marginBottom: '4px'
                    }}>
                      {campaign.title || 'Untitled Campaign'}
                    </div>
                    <div style={{
                      fontSize: '0.875rem',
                      color: '#6b7280'
                    }}>
                      {campaign.createdAt ? new Date(campaign.createdAt.toDate ? campaign.createdAt.toDate() : campaign.createdAt).toLocaleDateString() : 'Just now'}
                    </div>
                  </div>
                  {selectedCampaign?.id === campaign.id && (
                    <div style={{
                      color: '#6366f1',
                      fontWeight: '600'
                    }}>
                      Selected
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* BELOW THAT: Campaign Content with TABS */}
        {selectedCampaign && (
          <div style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '40px'
          }}>
            {/* Tab Navigation */}
            <div style={{
              display: 'flex',
              gap: '8px',
              borderBottom: '2px solid #e5e7eb',
              marginBottom: '24px'
            }}>
              {['blog', 'facebook', 'instagram', 'linkedin', 'twitter'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    padding: '12px 24px',
                    border: 'none',
                    backgroundColor: activeTab === tab ? '#6366f1' : 'transparent',
                    color: activeTab === tab ? '#ffffff' : '#6b7280',
                    fontWeight: activeTab === tab ? '600' : '400',
                    cursor: 'pointer',
                    borderRadius: '8px 8px 0 0',
                    transition: 'all 0.2s ease',
                    fontSize: '0.95rem'
                  }}
                  onMouseEnter={(e) => {
                    if (activeTab !== tab) {
                      e.currentTarget.style.backgroundColor = '#f3f4f6';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeTab !== tab) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            {activeTab === 'blog' && (
              <div>
                <div className="form-group">
                  <label className="form-label">Title</label>
                  <input
                    type="text"
                    className="form-input"
                    value={selectedCampaign.blogPost?.title || ''}
                    onChange={(e) => {
                      setSelectedCampaign({
                        ...selectedCampaign,
                        blogPost: { ...selectedCampaign.blogPost, title: e.target.value }
                      });
                    }}
                    placeholder="Blog post title..."
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Content</label>
                  <textarea
                    className="form-textarea"
                    value={selectedCampaign.blogPost?.content || ''}
                    onChange={(e) => {
                      setSelectedCampaign({
                        ...selectedCampaign,
                        blogPost: { ...selectedCampaign.blogPost, content: e.target.value }
                      });
                    }}
                    placeholder="Blog post content..."
                    style={{ minHeight: '300px' }}
                  />
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '20px',
                  paddingTop: '20px',
                  borderTop: '1px solid #e5e7eb'
                }}>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    {selectedCampaign.publishingStatus?.blog ? (
                      <span style={{ color: '#10b981', fontWeight: '600' }}>Posted ✓ {selectedCampaign.blogPost?.publishedAt ? new Date(selectedCampaign.blogPost.publishedAt.toDate ? selectedCampaign.blogPost.publishedAt.toDate() : selectedCampaign.blogPost.publishedAt).toLocaleString() : ''}</span>
                    ) : (
                      <span style={{ color: '#6b7280' }}>Not Posted ○</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      className="post-button"
                      onClick={handleSaveCampaign}
                      disabled={loading}
                      style={{ marginTop: 0, backgroundColor: '#6366f1' }}
                    >
                      Save Changes
                    </button>
                    <button
                      className="post-button"
                      onClick={async () => {
                        // First save the campaign
                        await handleSaveCampaign();
                        // Then publish to WordPress
                        await handleSaveBlogPost();
                      }}
                      disabled={loading || !selectedCampaign.blogPost?.title || !selectedCampaign.blogPost?.content}
                      style={{ marginTop: 0, backgroundColor: '#10b981' }}
                    >
                      {selectedCampaign.publishingStatus?.blog ? 'Update Post' : 'Post to Blog'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'facebook' && (
              <div>
                <div className="form-group">
                  <label className="form-label">Content</label>
                  <textarea
                    className="form-textarea"
                    value={selectedCampaign.socialMedia?.facebook?.post || ''}
                    onChange={(e) => {
                      setSelectedCampaign({
                        ...selectedCampaign,
                        socialMedia: {
                          ...selectedCampaign.socialMedia,
                          facebook: { ...selectedCampaign.socialMedia?.facebook, post: e.target.value }
                        }
                      });
                    }}
                    placeholder="Facebook post content..."
                    style={{ minHeight: '200px' }}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Link URL (optional)</label>
                  <input
                    type="url"
                    className="form-input"
                    value={selectedCampaign.socialMedia?.facebook?.linkUrl || ''}
                    onChange={(e) => {
                      setSelectedCampaign({
                        ...selectedCampaign,
                        socialMedia: {
                          ...selectedCampaign.socialMedia,
                          facebook: { ...selectedCampaign.socialMedia?.facebook, linkUrl: e.target.value }
                        }
                      });
                    }}
                    placeholder="https://logicpros.ca/blog/your-post"
                  />
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '20px',
                  paddingTop: '20px',
                  borderTop: '1px solid #e5e7eb'
                }}>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    {selectedCampaign.publishingStatus?.facebook ? (
                      <span style={{ color: '#10b981', fontWeight: '600' }}>Posted ✓ {selectedCampaign.socialMedia?.facebook?.publishedAt ? new Date(selectedCampaign.socialMedia.facebook.publishedAt.toDate ? selectedCampaign.socialMedia.facebook.publishedAt.toDate() : selectedCampaign.socialMedia.facebook.publishedAt).toLocaleString() : ''}</span>
                    ) : (
                      <span style={{ color: '#6b7280' }}>Not Posted ○</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      className="post-button"
                      onClick={handleSaveCampaign}
                      disabled={loading}
                      style={{ marginTop: 0, backgroundColor: '#6366f1' }}
                    >
                      Save Changes
                    </button>
                    <button
                      className="post-button"
                      onClick={async () => {
                        await handleSaveCampaign();
                        // TODO: Add Facebook post handler
                        setMessage('Facebook posting coming soon!');
                      }}
                      disabled={loading || !selectedCampaign.socialMedia?.facebook?.post}
                      style={{ marginTop: 0, backgroundColor: '#1877F2' }}
                    >
                      {selectedCampaign.publishingStatus?.facebook ? 'Update Post' : 'Post to Facebook'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'instagram' && (
              <div>
                <div className="form-group">
                  <label className="form-label">Caption</label>
                  <textarea
                    className="form-textarea"
                    value={selectedCampaign.socialMedia?.instagram?.caption || ''}
                    onChange={(e) => {
                      setSelectedCampaign({
                        ...selectedCampaign,
                        socialMedia: {
                          ...selectedCampaign.socialMedia,
                          instagram: { ...selectedCampaign.socialMedia?.instagram, caption: e.target.value }
                        }
                      });
                    }}
                    placeholder="Instagram caption..."
                    style={{ minHeight: '200px' }}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Image URL</label>
                  <input
                    type="url"
                    className="form-input"
                    value={selectedCampaign.socialMedia?.instagram?.imageUrl || ''}
                    onChange={(e) => {
                      setSelectedCampaign({
                        ...selectedCampaign,
                        socialMedia: {
                          ...selectedCampaign.socialMedia,
                          instagram: { ...selectedCampaign.socialMedia?.instagram, imageUrl: e.target.value }
                        }
                      });
                    }}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '20px',
                  paddingTop: '20px',
                  borderTop: '1px solid #e5e7eb'
                }}>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    {selectedCampaign.publishingStatus?.instagram ? (
                      <span style={{ color: '#10b981', fontWeight: '600' }}>Posted ✓ {selectedCampaign.socialMedia?.instagram?.publishedAt ? new Date(selectedCampaign.socialMedia.instagram.publishedAt.toDate ? selectedCampaign.socialMedia.instagram.publishedAt.toDate() : selectedCampaign.socialMedia.instagram.publishedAt).toLocaleString() : ''}</span>
                    ) : (
                      <span style={{ color: '#6b7280' }}>Not Posted ○</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      className="post-button"
                      onClick={handleSaveCampaign}
                      disabled={loading}
                      style={{ marginTop: 0, backgroundColor: '#6366f1' }}
                    >
                      Save Changes
                    </button>
                    <button
                      className="post-button"
                      onClick={async () => {
                        await handleSaveCampaign();
                        // TODO: Add Instagram post handler
                        setMessage('Instagram posting coming soon!');
                      }}
                      disabled={loading || !selectedCampaign.socialMedia?.instagram?.caption}
                      style={{ marginTop: 0, backgroundColor: '#E4405F' }}
                    >
                      {selectedCampaign.publishingStatus?.instagram ? 'Update Post' : 'Post to Instagram'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'linkedin' && (
              <div>
                <div className="form-group">
                  <label className="form-label">Content</label>
                  <textarea
                    className="form-textarea"
                    value={selectedCampaign.socialMedia?.linkedin?.post || ''}
                    onChange={(e) => {
                      setSelectedCampaign({
                        ...selectedCampaign,
                        socialMedia: {
                          ...selectedCampaign.socialMedia,
                          linkedin: { ...selectedCampaign.socialMedia?.linkedin, post: e.target.value }
                        }
                      });
                    }}
                    placeholder="LinkedIn post content..."
                    style={{ minHeight: '200px' }}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Visibility</label>
                  <select
                    className="form-input"
                    value={selectedCampaign.socialMedia?.linkedin?.visibility || 'PUBLIC'}
                    onChange={(e) => {
                      setSelectedCampaign({
                        ...selectedCampaign,
                        socialMedia: {
                          ...selectedCampaign.socialMedia,
                          linkedin: { ...selectedCampaign.socialMedia?.linkedin, visibility: e.target.value }
                        }
                      });
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <option value="PUBLIC">Public</option>
                    <option value="CONNECTIONS">Connections Only</option>
                  </select>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '20px',
                  paddingTop: '20px',
                  borderTop: '1px solid #e5e7eb'
                }}>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    {selectedCampaign.publishingStatus?.linkedin ? (
                      <span style={{ color: '#10b981', fontWeight: '600' }}>Posted ✓ {selectedCampaign.socialMedia?.linkedin?.publishedAt ? new Date(selectedCampaign.socialMedia.linkedin.publishedAt.toDate ? selectedCampaign.socialMedia.linkedin.publishedAt.toDate() : selectedCampaign.socialMedia.linkedin.publishedAt).toLocaleString() : ''}</span>
                    ) : (
                      <span style={{ color: '#6b7280' }}>Not Posted ○</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      className="post-button"
                      onClick={handleSaveCampaign}
                      disabled={loading}
                      style={{ marginTop: 0, backgroundColor: '#6366f1' }}
                    >
                      Save Changes
                    </button>
                    <button
                      className="post-button"
                      onClick={async () => {
                        await handleSaveCampaign();
                        // Call existing LinkedIn post handler
                        await handlePostToLinkedIn();
                      }}
                      disabled={loading || !linkedinConnected || !selectedCampaign.socialMedia?.linkedin?.post}
                      style={{ marginTop: 0, backgroundColor: '#0A66C2' }}
                    >
                      {selectedCampaign.publishingStatus?.linkedin ? 'Update Post' : 'Post to LinkedIn'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'twitter' && (
              <div>
                <div className="form-group">
                  <label className="form-label">Thread</label>
                  <textarea
                    className="form-textarea"
                    value={(selectedCampaign.socialMedia?.twitter?.thread || []).join('\n\n---\n\n')}
                    onChange={(e) => {
                      const thread = e.target.value.split('\n\n---\n\n');
                      setSelectedCampaign({
                        ...selectedCampaign,
                        socialMedia: {
                          ...selectedCampaign.socialMedia,
                          twitter: { ...selectedCampaign.socialMedia?.twitter, thread }
                        }
                      });
                    }}
                    placeholder="Twitter thread... (separate tweets with --- on its own line)"
                    style={{ minHeight: '200px' }}
                  />
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '20px',
                  paddingTop: '20px',
                  borderTop: '1px solid #e5e7eb'
                }}>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    {selectedCampaign.publishingStatus?.twitter ? (
                      <span style={{ color: '#10b981', fontWeight: '600' }}>Posted ✓ {selectedCampaign.socialMedia?.twitter?.publishedAt ? new Date(selectedCampaign.socialMedia.twitter.publishedAt.toDate ? selectedCampaign.socialMedia.twitter.publishedAt.toDate() : selectedCampaign.socialMedia.twitter.publishedAt).toLocaleString() : ''}</span>
                    ) : (
                      <span style={{ color: '#6b7280' }}>Not Posted ○</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      className="post-button"
                      onClick={handleSaveCampaign}
                      disabled={loading}
                      style={{ marginTop: 0, backgroundColor: '#6366f1' }}
                    >
                      Save Changes
                    </button>
                    <button
                      className="post-button"
                      onClick={async () => {
                        await handleSaveCampaign();
                        // TODO: Add Twitter post handler
                        setMessage('Twitter posting coming soon!');
                      }}
                      disabled={loading || !selectedCampaign.socialMedia?.twitter?.thread || selectedCampaign.socialMedia?.twitter?.thread.length === 0}
                      style={{ marginTop: 0, backgroundColor: '#000000' }}
                    >
                      {selectedCampaign.publishingStatus?.twitter ? 'Update Thread' : 'Post to Twitter'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

      </div>

      {/* AI-Generated Posts History Section */}
      {user && generatedPostsHistory.length > 0 && (
        <div className="posts-container history-container">
          <div className="history-header">
            <h2 className="history-header">
              🤖 AI-Generated Content History
            </h2>
            <button
              className="history-toggle-button"
              onClick={() => setShowHistory(!showHistory)}
            >
              {showHistory ? 'Hide History' : 'Show History'}
            </button>
          </div>

          {showHistory && (
            <div className="history-content">
              <div className="posts-grid">
                {generatedPostsHistory.map(post => {
                  // Parse the post data - support both old flat and new nested structures
                  const blogTitle = post.blogPost?.title || post.polished_title;
                  const blogContent = post.blogPost?.content || post.polished_post;
                  const blogExcerpt = post.blogPost?.excerpt;
                  const blogSlug = post.blogPost?.slug;
                  const featuredImage = post.blogPost?.featuredImageUrl || post.featured_image_url;

                  const instagramCaption = post.socialMedia?.instagram?.caption || post.instagram_caption;
                  const instagramHashtags = post.socialMedia?.instagram?.hashtags || post.instagram_hashtags;

                  const facebookPost = post.socialMedia?.facebook?.post || post.facebook_post;
                  const linkedinPost = post.socialMedia?.linkedin?.post || post.linkedin_post;
                  const twitterThread = post.socialMedia?.twitter?.thread || post.twitter_thread;

                  return (
                    <div key={post.id} className="post-card" style={{ border: '2px solid #6366F1' }}>
                      <div className="platform-header" style={{ backgroundColor: '#6366F1', color: 'white' }}>
                        <span className="platform-icon">🤖</span>
                        <span className="platform-name">AI-Generated Post Bundle</span>
                      </div>

                      <div className="history-post-content">
                        {/* Blog Post Section */}
                        {blogTitle && blogContent && (
                          <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                            <h4 style={{ margin: '0 0 10px 0', color: '#1a1a2e', fontSize: '1.1rem' }}>📝 Blog Post</h4>
                            <p style={{ fontWeight: 'bold', margin: '5px 0' }}>{blogTitle}</p>
                            {blogExcerpt && <p style={{ fontSize: '0.9rem', color: '#666', fontStyle: 'italic' }}>{blogExcerpt}</p>}
                            <p style={{ fontSize: '0.9rem', marginTop: '10px' }}>{blogContent.substring(0, 200)}...</p>
                            {blogSlug && <p style={{ fontSize: '0.8rem', color: '#888' }}>Slug: {blogSlug}</p>}
                            {featuredImage && (
                              <img
                                src={featuredImage}
                                alt="Featured"
                                className="history-post-image"
                                style={{ maxWidth: '100%', marginTop: '10px', borderRadius: '4px' }}
                              />
                            )}
                          </div>
                        )}

                        {/* Instagram Section */}
                        {instagramCaption && (
                          <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#fef3f8', borderRadius: '8px' }}>
                            <h5 style={{ margin: '0 0 8px 0', color: '#E4405F' }}>📸 Instagram</h5>
                            <p style={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{instagramCaption}</p>
                            {instagramHashtags && instagramHashtags.length > 0 && (
                              <p style={{ fontSize: '0.75rem', color: '#E4405F', marginTop: '8px' }}>
                                {instagramHashtags.join(' ')}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Facebook Section */}
                        {facebookPost && (
                          <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#f0f5ff', borderRadius: '8px' }}>
                            <h5 style={{ margin: '0 0 8px 0', color: '#1877F2' }}>📘 Facebook</h5>
                            <p style={{ fontSize: '0.85rem' }}>{facebookPost}</p>
                          </div>
                        )}

                        {/* LinkedIn Section */}
                        {linkedinPost && (
                          <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#f3f7fb', borderRadius: '8px' }}>
                            <h5 style={{ margin: '0 0 8px 0', color: '#0A66C2' }}>💼 LinkedIn</h5>
                            <p style={{ fontSize: '0.85rem' }}>{linkedinPost}</p>
                          </div>
                        )}

                        {/* Twitter/X Section */}
                        {twitterThread && Array.isArray(twitterThread) && (
                          <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                            <h5 style={{ margin: '0 0 8px 0', color: '#000' }}>𝕏 Twitter/X Thread ({twitterThread.length} tweets)</h5>
                            {twitterThread.map((tweet, idx) => (
                              <p key={idx} style={{ fontSize: '0.85rem', marginBottom: '8px', paddingBottom: '8px', borderBottom: idx < twitterThread.length - 1 ? '1px solid #ddd' : 'none' }}>
                                {idx + 1}. {tweet}
                              </p>
                            ))}
                          </div>
                        )}

                        <div className="history-post-meta" style={{ borderTop: '1px solid #eee', paddingTop: '10px', marginTop: '15px' }}>
                          <span style={{ fontSize: '0.85rem', color: '#666' }}>
                            Generated: {post.createdAt ? new Date(post.createdAt.toDate ? post.createdAt.toDate() : post.createdAt).toLocaleString() : 'Just now'}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      <Footer />
      </div>
    </>
  );
};